import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { enUS, es } from 'date-fns/locale';
import { endOfDay, isAfter, isBefore, parseISO, startOfDay } from 'date-fns';
import {
  Avatar, Box, Button, Chip, CircularProgress, IconButton, Pagination, Stack, Tooltip, Typography, Unstable_Grid2 as Grid
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CalendarMonth as CalendarIcon, CheckCircle as CheckIcon, EventBusy as AbsentIcon,
  InsertChartOutlined as ChartIcon, Event as AddCalendarIcon, Visibility as DetailIcon, Close as CloseIcon,
  DeleteOutline as DeleteIcon
} from '@mui/icons-material';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import SessionDialogCreate from 'components/session/dialog/create/SessionCreateDialog';
import SessionDetailDrawer from 'components/session/SessionDetailDrawer';
import SessionFilters from 'components/session/SessionFilters';
import SessionListItem from 'components/session/SessionListItem';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import SessionDrawerListItem from 'components/session/SessionDrawerListItem';
import SessionDetailPanel from 'components/session/SessionDetailPanel';
import SessionCounters from 'components/session/SessionCounters';
import StatusGroupHeader, { STATUS_GROUP_META, STATUS_GROUP_ORDER } from 'components/session/SessionStatusGroupHeader';
import { timeFormat } from 'common/constants/date';
import { convertUtcToLocalTime } from 'common/utils/date';
import { normalizeSessionStatus, getStatusLabel, dimIfPastSx } from 'common/utils/sessionColor';
import { isMembershipActive } from 'common/utils/session';
import { sessionStatus } from 'common/constants/options';
import routes from 'common/routes';
import { isValidId } from 'common/utils/id';
import AppBarContext from 'context/AppBarContext';
import groupSessionSlice from 'store/modules/groupSession';
import clientPaymentSlice from 'store/modules/clientPayment';
import sessionSlice from 'store/modules/session';

const INITIAL_FILTERS = { trainerId: '', status: '', timeRange: '', date: {} };
const ORDER = STATUS_GROUP_ORDER;
const CLIENT_PER_PAGE = 25;

const getDisplayStatus = (session, currentUserId) => {
  const mine = session.attendances?.find((a) => String(a.user?.id) === String(currentUserId));
  return mine?.status ?? session.status;
};
const rangeLabel = (s) => `${convertUtcToLocalTime(s.start_date, timeFormat)} – ${convertUtcToLocalTime(s.end_date, timeFormat)}`;

function NextSessionHero({ session, dateLocale, isActivePlus, membershipActive, onAddToCalendar, onViewDetail }) {
  const theme = useTheme();
  const { t } = useTranslation(['sessions', 'labels']);
  const trainer = session.trainer || session.trainer_id;
  const weekday = convertUtcToLocalTime(session.start_date, 'EEEE', dateLocale);
  const day = convertUtcToLocalTime(session.start_date, 'd', dateLocale);
  const monthYear = convertUtcToLocalTime(session.start_date, 'MMM yyyy', dateLocale);

  return (
    <Box sx={{ background: theme.palette.brand.heroGradient, border: `1px solid ${theme.palette.brand.heroBorder}`, borderRadius: '18px', p: { xs: 2.5, md: 3 }, mb: 3, color: theme.palette.brand.heroText }}>
      <Typography variant="caption" sx={{ color: theme.palette.gold.bright, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 800 }}>
        {t('nextSession', { ns: 'sessions', defaultValue: 'Próxima sesión' })}
      </Typography>
      <Grid container spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
        <Grid xs={12} md={7}>
          <Stack direction="row" spacing={2.5} alignItems="center" flexWrap="wrap">
            <Box sx={{ textAlign: 'center', minWidth: 72 }}>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.8 }}>{weekday}</Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1 }}>{day}</Typography>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', opacity: 0.8 }}>{monthYear}</Typography>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{rangeLabel(session)}</Typography>
              {trainer && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <Avatar src={trainer.photo} alt={trainer.full_name} sx={{ width: 26, height: 26, fontSize: '0.72rem' }}>{trainer.full_name?.[0]}</Avatar>
                  <Typography variant="body2" sx={{ opacity: 0.92 }}>{trainer.full_name}</Typography>
                </Stack>
              )}
              {session.session_type_id?.name && (
                <Chip label={session.session_type_id.name} size="small" sx={{ mt: 1, backgroundColor: theme.palette.gold.soft, color: theme.palette.gold.bright, fontWeight: 700, border: `1px solid ${theme.palette.gold.bright}55` }} />
              )}
            </Box>
          </Stack>
        </Grid>
        <Grid xs={12} md={5}>
          {/* Mensaje motivacional SOLO para Active Plus (clases en el club). */}
          {isActivePlus && (
            <Typography variant="body2" sx={{ opacity: 0.78, mb: 1.5, textAlign: { md: 'right' } }}>
              {t('nextSessionHint', { ns: 'sessions', defaultValue: 'Prepárate para tu próxima clase. ¡Nos vemos en el club!' })}
            </Typography>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent={{ md: 'flex-end' }}>
            {/* Sin membresía vigente (en mora/inactivo) no se pueden crear clases: botón deshabilitado. */}
            <Tooltip title={!membershipActive ? t('createSessionBlockedArrears', { ns: 'sessions', defaultValue: 'Tu membresía está en mora o inactiva. Renuévala para crear clases.' }) : ''}>
              <span>
                <Button variant="contained" color="primary" startIcon={<AddCalendarIcon />} onClick={onAddToCalendar} disabled={!membershipActive} sx={{ textTransform: 'none', fontWeight: 700 }}>
                  {t('createSession', { ns: 'sessions', defaultValue: 'Crear sesión' })}
                </Button>
              </span>
            </Tooltip>
            <Button variant="outlined" color="inherit" startIcon={<DetailIcon />} onClick={onViewDetail} sx={{ color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.5)', textTransform: 'none', fontWeight: 700 }}>
              {t('viewDetail', { ns: 'sessions', defaultValue: 'Ver detalle' })}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
NextSessionHero.propTypes = {
  session: PropTypes.object.isRequired,
  dateLocale: PropTypes.object.isRequired,
  isActivePlus: PropTypes.bool.isRequired,
  membershipActive: PropTypes.bool.isRequired,
  onAddToCalendar: PropTypes.func.isRequired,
  onViewDetail: PropTypes.func.isRequired
};

function ActivePlusSessionsPage({ mode = 'group' }) {
  const isGroup = mode === 'group';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t, i18n } = useTranslation(['labels', 'options', 'routes', 'sessions', 'tables']);
  const dateLocale = i18n.language === 'es' ? es : enUS;
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const pageTitle = isGroup ? t('session.groupSessions', { ns: 'routes' }) : t('session.mySessions', { ns: 'routes' });

  const groupSessions = useSelector((state) => state.groupSession.data.myGroupSessions);
  const storeStatus = useSelector((state) => state.groupSession.status.general);
  const currentUserId = useSelector((state) => state.auth.user.id);
  const isActivePlus = useSelector((state) => state.auth.user.is_active_plus);
  const membershipStatus = useSelector((state) => state.auth.user?.membership_status);
  const individualSessions = useSelector((state) => state.session.data.list) || [];
  const sessionStoreStatus = useSelector((state) => state.session.status.general);
  const sessionRefresh = useSelector((state) => state.session.refresh);
  const selectedSessionId = useSelector((state) => state.groupSession.selectedSessionId);
  const detailMode = useSelector((state) => state.groupSession.detailMode);
  const listFilter = useSelector((state) => state.groupSession.listFilter);
  const cameFromList = useSelector((state) => state.groupSession.cameFromList);

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [createOpen, setCreateOpen] = useState(false);
  const [page, setPage] = useState(1);
  // Borrar una sesión propia ("Mis sesiones" individuales): confirmación + quita al instante.
  const [deleteId, setDeleteId] = useState(null);
  const [deletedIds, setDeletedIds] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const confirmDelete = useCallback(() => {
    if (deleteId == null) return;
    dispatch(sessionSlice.actions.forceDelete([deleteId]));
    setDeletedIds((prev) => [...prev, String(deleteId)]);
    setDeleteId(null);
  }, [deleteId, dispatch]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  // "Sesiones grupales" (grupo, Active Plus) vs "Mis sesiones" (individuales/personalizadas con plan).
  useEffect(() => {
    if (isGroup) dispatch(groupSessionSlice.actions.fetchMyGroupSessions());
    return () => { dispatch(groupSessionSlice.actions.clearSelection()); };
  }, [dispatch, isGroup]);

  useEffect(() => {
    if (!currentUserId) return;
    dispatch(clientPaymentSlice.actions.getActivePayments(currentUserId));
    if (!isGroup) {
      dispatch(sessionSlice.actions.list({ pagination: { first: 100, page: 1 }, filters: { client_id: currentUserId } }));
    }
  }, [dispatch, currentUserId, isGroup]);

  // Refresco inmediato al crear/borrar una sesión (el flag `refresh` lo setean create/forceDelete),
  // sin tener que cambiar de vista. `list` resetea el flag, así que no entra en bucle.
  useEffect(() => {
    if (sessionRefresh && currentUserId && !isGroup) {
      dispatch(sessionSlice.actions.list({ pagination: { first: 100, page: 1 }, filters: { client_id: currentUserId } }));
    }
  }, [sessionRefresh, currentUserId, isGroup, dispatch]);

  const items = useMemo(() => {
    const source = isGroup
      ? groupSessions.map((s) => ({ session: s, displayStatus: getDisplayStatus(s, currentUserId) }))
      : individualSessions.map((s) => ({ session: s, displayStatus: s.status }));
    return source
      .filter((it) => !deletedIds.includes(String(it.session.id)))
      .map((it) => ({ ...it, norm: normalizeSessionStatus(it.displayStatus, it.session.start_date), date: new Date(it.session.start_date).getTime() }))
      .sort((a, b) => a.date - b.date);
  }, [isGroup, groupSessions, individualSessions, currentUserId, deletedIds]);

  const counts = useMemo(() => items.reduce((acc, it) => {
    if (it.norm === 'scheduled' || it.norm === 'pending') acc.scheduled += 1;
    else if (it.norm === 'completed') acc.completed += 1;
    else if (it.norm === 'absent') acc.absent += 1;
    return acc;
  }, { scheduled: 0, completed: 0, absent: 0 }), [items]);

  const attendancePct = counts.completed + counts.absent > 0 ? Math.round((counts.completed / (counts.completed + counts.absent)) * 100) : 0;

  // OJO: las sesiones individuales traen `trainer_id` SIN `id` → se identifica al entrenador
  // por `full_name` para que TODOS los entrenadores (grupales e individuales) salgan en el filtro.
  const trainerOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    items.forEach(({ session: s }) => {
      const tr = s.trainer || s.trainer_id;
      const name = tr?.full_name;
      if (name && !seen.has(name)) { seen.add(name); opts.push({ value: name, label: name }); }
    });
    return opts;
  }, [items]);

  const timeRangeOptions = useMemo(() => {
    const set = new Set();
    items.forEach(({ session: s }) => set.add(rangeLabel(s)));
    return [...set].sort();
  }, [items]);

  const statusOptions = [
    { value: 'all', label: t('allStatuses', { ns: 'sessions', defaultValue: 'Todos los estados' }) },
    { value: 'scheduled', label: t('statusScheduledPlural', { ns: 'sessions', defaultValue: 'Programadas' }) },
    { value: 'completed', label: t('statusCompletedPlural', { ns: 'sessions', defaultValue: 'Completadas' }) },
    { value: 'absent', label: t('absences', { ns: 'sessions', defaultValue: 'Ausencias' }) }
  ];

  // Orden: PROGRAMADAS primero (próximas, asc), luego pendientes, luego completadas/ausencias
  // (recientes primero). Así el cliente ve primero lo que viene.
  const filteredItems = useMemo(() => items
    .filter(({ session: s, norm }) => {
      if (filters.status && !(norm === filters.status || (filters.status === 'scheduled' && norm === 'pending'))) return false;
      const tr = s.trainer || s.trainer_id;
      if (filters.trainerId && tr?.full_name !== filters.trainerId) return false;
      if (filters.timeRange && rangeLabel(s) !== filters.timeRange) return false;
      if (filters.date?.start_date && isBefore(parseISO(s.start_date), startOfDay(parseISO(filters.date.start_date)))) return false;
      if (filters.date?.end_date && isAfter(parseISO(s.start_date), endOfDay(parseISO(filters.date.end_date)))) return false;
      return true;
    })
    .sort((a, b) => {
      const oa = ORDER[a.norm] ?? 3;
      const ob = ORDER[b.norm] ?? 3;
      if (oa !== ob) return oa - ob;
      return oa <= 1 ? a.date - b.date : b.date - a.date;
    }), [items, filters]);

  const pageCount = Math.ceil(filteredItems.length / CLIENT_PER_PAGE) || 1;
  const pagedItems = useMemo(
    () => filteredItems.slice((page - 1) * CLIENT_PER_PAGE, page * CLIENT_PER_PAGE),
    [filteredItems, page]
  );

  // Borrado masivo de PROGRAMADAS (solo sesiones individuales, que son las borrables por el cliente).
  const schedulableIds = useMemo(
    () => filteredItems
      .filter((it) => !isGroup && (it.norm === 'scheduled' || it.norm === 'pending'))
      .map((it) => String(it.session.id)),
    [filteredItems, isGroup]
  );
  const toggleSelect = useCallback((id) => {
    const key = String(id);
    setSelectedIds((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  }, []);
  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => (prev.length === schedulableIds.length ? [] : schedulableIds));
  }, [schedulableIds]);
  const exitSelectMode = useCallback(() => { setSelectMode(false); setSelectedIds([]); }, []);
  const confirmBulkDelete = useCallback(() => {
    if (selectedIds.length === 0) return;
    dispatch(sessionSlice.actions.forceDelete(selectedIds));
    setDeletedIds((prev) => [...prev, ...selectedIds]);
    setSelectedIds([]);
    setSelectMode(false);
    setBulkDeleteOpen(false);
  }, [selectedIds, dispatch]);

  const counterListItems = useMemo(() => {
    if (!listFilter) return [];
    return filteredItems.filter(({ norm }) => norm === listFilter || (listFilter === 'scheduled' && norm === 'pending'));
  }, [filteredItems, listFilter]);

  const nextSession = useMemo(() => items.find((it) => it.norm === 'scheduled' && isAfter(parseISO(it.session.start_date), new Date())), [items]);
  const selectedItem = useMemo(() => items.find((it) => String(it.session.id) === String(selectedSessionId)), [items, selectedSessionId]);

  const openDetail = useCallback((id) => dispatch(groupSessionSlice.actions.selectSession(id)), [dispatch]);
  const closePanel = useCallback(() => dispatch(groupSessionSlice.actions.clearSelection()), [dispatch]);
  const openCounterList = useCallback((key) => dispatch(groupSessionSlice.actions.showCounterList(key)), [dispatch]);
  const backToList = useCallback(() => dispatch(groupSessionSlice.actions.backToList()), [dispatch]);
  const goToProgress = useCallback(() => navigate(routes.clientProgress.path), [navigate]);
  const goToAgenda = useCallback((s) => {
    const day = convertUtcToLocalTime(s.start_date, 'yyyy-MM-dd');
    // Ver la nota de LargeSessionCard: un id inservible dejaba la agenda reintentando sin fin.
    const sessionParam = isValidId(s?.id) ? `&session=${s.id}` : '';
    navigate(`${routes.calendar.path}?date=${day}${sessionParam}`);
  }, [navigate]);

  const counters = [
    { key: 'scheduled', label: t('statusScheduledPlural', { ns: 'sessions', defaultValue: 'Programadas' }), value: counts.scheduled, sub: t('upcomingClasses', { ns: 'sessions', defaultValue: 'Próximas clases' }), color: theme.palette.sessionProximity.far, icon: <CalendarIcon /> },
    { key: 'completed', label: t('statusCompletedPlural', { ns: 'sessions', defaultValue: 'Completadas' }), value: counts.completed, sub: t('thisMonth', { ns: 'sessions', defaultValue: 'Este mes' }), color: theme.palette.sessionStatus.completed.fg, icon: <CheckIcon /> },
    { key: 'absent', label: t('absences', { ns: 'sessions', defaultValue: 'Ausencias' }), value: counts.absent, sub: t('thisMonth', { ns: 'sessions', defaultValue: 'Este mes' }), color: theme.palette.sessionStatus.absent.fg, icon: <AbsentIcon /> },
    { key: 'attendance', label: t('attendance', { ns: 'sessions', defaultValue: 'Asistencia' }), value: `${attendancePct}%`, sub: t('last30days', { ns: 'sessions', defaultValue: 'Últimos 30 días' }), color: theme.palette.gold.bright, icon: <ChartIcon />, disabled: true }
  ];

  const isLoading = (isGroup ? storeStatus : sessionStoreStatus) === 'loading' && items.length === 0;
  const progress = { completed: counts.completed, scheduled: counts.scheduled, absent: counts.absent };
  const panelOpen = detailMode === 'detail' ? Boolean(selectedItem) : detailMode === 'list';

  let drawerContent = <Box />;
  if (detailMode === 'detail' && selectedItem) {
    drawerContent = (
      <SessionDetailPanel
        session={selectedItem.session}
        displayStatus={selectedItem.displayStatus}
        dateLocale={dateLocale}
        progress={progress}
        onViewMore={goToProgress}
        onClose={closePanel}
        onBack={cameFromList ? backToList : null}
        onGoToAgenda={() => goToAgenda(selectedItem.session)}
      />
    );
  } else if (detailMode === 'list') {
    const listTitleStatus = { scheduled: 'SCHEDULED', completed: 'FINISHED', absent: 'ABSENT' }[listFilter] || 'SCHEDULED';
    drawerContent = (
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{getStatusLabel(t, listTitleStatus)}</Typography>
          <IconButton size="small" onClick={closePanel} sx={{ color: 'text.secondary' }}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
        <Stack spacing={1.25}>
          {counterListItems.length === 0
            ? <Typography variant="body2" color="text.secondary">{t('noSessionsFilter', { ns: 'sessions', defaultValue: 'No hay sesiones que mostrar.' })}</Typography>
            : counterListItems.map(({ session: s, displayStatus }) => (
              <SessionDrawerListItem key={s.id} session={s} displayStatus={displayStatus} dateLocale={dateLocale} onClick={() => openDetail(s.id)} />
            ))}
        </Stack>
      </Box>
    );
  }

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {isGroup
          ? t('groupClassesSubtitle', { ns: 'sessions', defaultValue: 'Tu agenda de clases grupales (Active Plus).' })
          : t('personalSessionsSubtitle', { ns: 'sessions', defaultValue: 'Tus sesiones personalizadas con tu plan.' })}
      </Typography>

      {nextSession && (
        // Membresía no vigente (mora/inactivo): se opaca la card de la próxima sesión.
        <Box sx={dimIfPastSx(!isMembershipActive(membershipStatus))}>
          <NextSessionHero session={nextSession.session} dateLocale={dateLocale} isActivePlus={isGroup && isActivePlus} membershipActive={isMembershipActive(membershipStatus)} onAddToCalendar={() => setCreateOpen(true)} onViewDetail={() => openDetail(nextSession.session.id)} />
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <SessionCounters counters={counters} activeKey={detailMode === 'list' ? listFilter : null} onSelect={openCounterList} />
      </Box>

      <SessionFilters trainerOptions={trainerOptions} timeRangeOptions={timeRangeOptions} statusOptions={statusOptions} onApply={(v) => { setFilters(v); setPage(1); }} />

      {!isGroup && schedulableIds.length > 0 && (
        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
          {!selectMode ? (
            <Button size="small" variant="text" color="inherit" startIcon={<DeleteIcon fontSize="small" />} onClick={() => setSelectMode(true)} sx={{ color: 'secondary.main', textTransform: 'none', fontWeight: 700 }}>
              {t('selectToDelete', { ns: 'sessions', defaultValue: 'Seleccionar para eliminar' })}
            </Button>
          ) : (
            <>
              <Typography variant="body2" sx={{ color: 'text.secondary', mr: 'auto' }}>
                {t('selectedCount', { ns: 'sessions', count: selectedIds.length, defaultValue: `${selectedIds.length} seleccionadas` })}
              </Typography>
              <Button size="small" variant="outlined" color="inherit" onClick={toggleSelectAll} sx={{ color: 'text.primary', borderColor: 'hairline.strong', textTransform: 'none', fontWeight: 700, '&:hover': { borderColor: 'hairline.strong', backgroundColor: 'action.hover' } }}>
                {selectedIds.length === schedulableIds.length
                  ? t('clearSelection', { ns: 'sessions', defaultValue: 'Quitar selección' })
                  : t('selectAll', { ns: 'sessions', defaultValue: 'Seleccionar todas' })}
              </Button>
              <Button size="small" variant="contained" color="error" disabled={selectedIds.length === 0} startIcon={<DeleteIcon fontSize="small" />} onClick={() => setBulkDeleteOpen(true)} sx={{ textTransform: 'none', fontWeight: 700 }}>
                {t('deleteSelected', { ns: 'sessions', defaultValue: 'Eliminar' })}
              </Button>
              <Button size="small" variant="text" color="inherit" onClick={exitSelectMode} sx={{ color: 'text.secondary', textTransform: 'none' }}>
                {t('cancel', { ns: 'sessions', defaultValue: 'Cancelar' })}
              </Button>
            </>
          )}
        </Stack>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: 'secondary.main' }} /></Box>
      ) : (
        <>
          {/* Membresía no vigente: se opacan TODAS las cards de sesión (Active Plus en mora/inactivo). */}
          <Stack spacing={1.5} sx={{ mt: 2, ...dimIfPastSx(!isMembershipActive(membershipStatus)) }}>
            {filteredItems.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body2" color="text.secondary">{t('noSessionsFilter', { ns: 'sessions', defaultValue: 'No hay sesiones que mostrar. Intenta cambiando los filtros' })}</Typography>
              </Box>
            ) : (() => {
              // Inserta un encabezado-divisor cada vez que cambia el grupo de estado
              // (la lista ya viene ordenada por ORDER, así que los grupos son contiguos).
              let lastGroup = null;
              const nodes = [];
              pagedItems.forEach(({ session: s, displayStatus, norm }) => {
                if (norm !== lastGroup) {
                  const meta = STATUS_GROUP_META[norm] || STATUS_GROUP_META.scheduled;
                  nodes.push(
                    <StatusGroupHeader
                      key={`grp-${norm}-${s.id}`}
                      label={t(meta.key, { ns: 'sessions', defaultValue: meta.def })}
                      color={theme.palette.sessionStatus[meta.path].fg}
                      first={nodes.length === 0}
                    />
                  );
                  lastGroup = norm;
                }
                const isSchedulable = !isGroup && (norm === 'scheduled' || norm === 'pending');
                nodes.push(
                  <SessionListItem
                    key={s.id}
                    session={s}
                    displayStatus={displayStatus}
                    dateLocale={dateLocale}
                    selected={String(s.id) === String(selectedSessionId)}
                    actionLabel={selectMode ? null : t('viewDetail', { ns: 'sessions', defaultValue: 'Ver detalle' })}
                    onAction={() => openDetail(s.id)}
                    onDelete={!selectMode && !isGroup && displayStatus === sessionStatus.scheduled ? () => setDeleteId(s.id) : undefined}
                    selectable={selectMode && isSchedulable}
                    checked={selectedIds.includes(String(s.id))}
                    onToggleSelect={() => toggleSelect(s.id)}
                  />
                );
              });
              return nodes;
            })()}
          </Stack>
          {pageCount > 1 && (
            <Stack alignItems="center" sx={{ mt: 3 }}>
              <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} color="secondary" shape="rounded" />
            </Stack>
          )}
        </>
      )}

      <SessionDetailDrawer open={panelOpen} onClose={closePanel}>{drawerContent}</SessionDetailDrawer>
      {createOpen && <SessionDialogCreate open onClose={() => setCreateOpen(false)} />}
      <ConfirmDeleteModal
        open={deleteId != null}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        title={t('deleteSessionTitle', { ns: 'sessions', defaultValue: 'Eliminar sesión' })}
        message={t('deleteSessionMessage', { ns: 'sessions', defaultValue: '¿Seguro que quieres eliminar esta sesión? Se liberará el cupo de sesión.' })}
        count={1}
      />
      <ConfirmDeleteModal
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={confirmBulkDelete}
        title={t('deleteSessionsTitle', { ns: 'sessions', defaultValue: 'Eliminar sesiones' })}
        message={t('deleteSessionsMessage', { ns: 'sessions', count: selectedIds.length, defaultValue: '¿Seguro que quieres eliminar las sesiones seleccionadas? Se liberarán los cupos.' })}
        count={selectedIds.length}
      />
    </>
  );
}

ActivePlusSessionsPage.propTypes = { mode: PropTypes.oneOf(['group', 'individual']) };

export default ActivePlusSessionsPage;
